import $ from 'jquery';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import { get } from '../lib/props';
import Keyboard from './keyboard';
import registerJQueryPlugin from '../lib/register-jquery-plugin';
import mergeOptions from '../lib/merge-options';

export const PLUGIN_NAME = 'horizontalScroll';
export const DEFAULTS = {
  type: '',
  stacked: false,
  verticalLazy: get('rcProps.abTests.ab776', false),
  preserveScrollPosition: false,
  preserveScrollKey: '',
};

const BASE_SELECTOR = '.horizontal-scroll';
const WRAPPER_SELECTOR = `${BASE_SELECTOR}__wrapper`;
const SLIDES_SELECTOR = `${BASE_SELECTOR}__item`;
const SCROLL_POSITION_STORAGE_PREFIX = 'horizontalScroll_pos_';
const $window = $(window);

const isRTL = $('html').attr('dir') === 'rtl';

/**
 * Checks if the container is in the window viewport, preventing lazy image load
 * when section is below the fold
 * @param {jQuery} $el - The horizontal scroll container
 */
const isContainerInWindowViewport = $el => {
  const rect = $el[0].getBoundingClientRect();

  return rect.top < window.innerHeight && rect.bottom > 0;
};

const isInViewport = ($img, $container) => {
  // Cache the container properties once, assuming they don't change often.
  const containerOffset = $container.offset();
  const containerWidth = $container.outerWidth();
  const containerHeight = $container.height();

  // Get the image's position and size.
  const imgOffset = $img.offset();
  const imgWidth = $img.outerWidth();
  const imgHeight = $img.outerHeight();

  // Calculate image boundaries
  const imgTop = imgOffset.top;
  const imgBottom = imgTop + imgHeight;
  const imgLeft = imgOffset.left;
  const imgRight = imgLeft + imgWidth;

  // Calculate container boundaries
  const vpTop = containerOffset.top;
  const vpBottom = vpTop + containerHeight;
  const vpLeft = containerOffset.left;
  const vpRight = vpLeft + containerWidth;

  // Check if the image is within the container's viewport
  return imgBottom > vpTop && imgTop < vpBottom && imgLeft < vpRight && imgRight > vpLeft;
};

/**
 * Indicates whether element is within horizontal viewport.
 * @param  {HTMLElement} slide  The DOM element of the slide.
 * @return {boolean}  Whether the element is within the horizontal viewport.
 */
function isInHorizontalViewport(slide) {
  const rect = slide.getBoundingClientRect();
  const FULL_TOLERANCE = 1;
  const BUFFER = 20;

  return {
    fullyInViewport:
      rect.left >= 0 && rect.right <= window.innerWidth + FULL_TOLERANCE,

    partiallyInViewport:
      rect.right > BUFFER && rect.left < window.innerWidth - BUFFER,
  };
}

const addButtonsToWrapper = ($el) => {
  const previousButtonHTML = isRTL
      ? `
    <button aria-label="previous page" class="previous">
        <span class="btn btn--circle">
          <span class="icon icon--sm icon--arrow-right">
              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                  <path d="M6.25 12a.75.75 0 0 1-.51-1.3L8.648 8 5.74 5.3A.75.75 0 1 1 6.76 4.2l3.5 3.25a.75.75 0 0 1 0 1.1l-3.5 3.25a.749.749 0 0 1-.51.2z"/>
              </svg>
          </span>
        </span>
      </button>
    `
      : `
  <button aria-label="previous page" class="previous">
    <span class="btn btn--circle">
          <span class="icon icon--sm icon--arrow-left">
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                  <path d="M9.75 12a.749.749 0 0 1-.51-.2l-3.5-3.25a.75.75 0 0 1 0-1.1l3.5-3.25a.75.75 0 0 1 1.02 1.1L7.352 8l2.908 2.7a.75.75 0 0 1-.51 1.3z"/>
        </svg>
      </span>
    </span>
  </button>
`;

  const nextButtonHTML = isRTL
      ? `
    <button aria-label="next page" class="next">
        <span class="btn btn--circle">
          <span class="icon icon--sm icon--arrow-left">
              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                  <path d="M9.75 12a.749.749 0 0 1-.51-.2l-3.5-3.25a.75.75 0 0 1 0-1.1l3.5-3.25a.75.75 0 0 1 1.02 1.1L7.352 8l2.908 2.7a.75.75 0 0 1-.51 1.3z"/>
              </svg>
          </span>
        </span>
      </button>
    `
      : `
    <button aria-label="next page" class="next">
        <span class="btn btn--circle">
          <span class="icon icon--sm icon--arrow-right">
              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                  <path d="M6.25 12a.75.75 0 0 1-.51-1.3L8.648 8 5.74 5.3A.75.75 0 1 1 6.76 4.2l3.5 3.25a.75.75 0 0 1 0 1.1l-3.5 3.25a.749.749 0 0 1-.51.2z"/>
              </svg>
          </span>
        </span>
      </button>
    `;

  if (isRTL) {
    $el.after(nextButtonHTML);
    $el.before(previousButtonHTML);
  } else {
    $el.before(previousButtonHTML);
    $el.after(nextButtonHTML);
  }
};
const wrapHorizontalScrollContent = ($el) => {
  $el.wrap('<div class="horizontal-scroll__wrapper" aria-label="Carousel" role="region" />');
  addButtonsToWrapper($el);
};
const FOCUSABLE_SELECTOR = 'a, button, input, select, [tabindex]';

/**
 * Removes focusable children from the tab order for partially visible slides.
 * The slide remains clickable but keyboard navigation is restricted.
 *
 * @param {jQuery} $slide
 * @return {void}
 */
const disableFocus = ($slide) => {
  $slide.find(FOCUSABLE_SELECTOR).attr('tabindex', '-1');
};

/**
 * Restores focusable children to the tab order when a slide becomes fully visible.
 *
 * @param {jQuery} $slide
 * @return {void}
 */
const enableFocus = ($slide) => {
  $slide.find(FOCUSABLE_SELECTOR).removeAttr('tabindex');
};

/**
 * Updates the inert and aria-hidden attributes for each slide based on viewport visibility.
 *
 * Three statuses:
 *   - full    → fully interactive, in tab order
 *   - partial → clickable (no inert), but aria-hidden and tabindex="-1" on children
 *   - hidden  → inert + aria-hidden (no interaction at all)
 *
 * Skips updates when the slide status has not changed since the last scroll tick.
 *
 * @return {void}
 */
const updateSlideState = ($el) => {
  const $slides = $el.find(SLIDES_SELECTOR);

  if (!$slides.length) return;

  $slides.each((_index, slide) => {
    const { fullyInViewport, partiallyInViewport } = isInHorizontalViewport(slide);
    const $slide = $(slide);
    let status = 'hidden';
    if (fullyInViewport) status = 'full';
    else if (partiallyInViewport) status = 'partial';

    if ($slide.data('hs-status') === status) return;
    $slide.data('hs-status', status);

    if (status === 'full') {
      $slide.removeAttr('inert').removeAttr('aria-hidden');
      enableFocus($slide);
    } else if (status === 'partial') {
      $slide.removeAttr('inert').attr('aria-hidden', 'true');
      disableFocus($slide);
    } else {
      $slide.attr('inert', '').attr('aria-hidden', 'true');
    }
  });
};
/**
 * Calculate combined width of all slides, whether stacked or not.
 * @param {jQuery} $slides  The slides
 * @param {Object} opts Options
 * @return {void}
 */
const calculateTotalScrollableWidth = ($slides, opts) => {
  if (opts.stacked) {
    // If stacked, calculate based on number of columns per row
    const firstSlideWidth = $slides.first().outerWidth(true);
    const visibleRows = Math.floor(
        $slides.parent().outerHeight() / $slides.first().outerHeight(true));
    const columnsPerRow = Math.ceil($slides.length / visibleRows);

    return firstSlideWidth * columnsPerRow;
  }

  // If unstacked, sum up total slides width
  return $slides.toArray().reduce(
      (totalWidth, slide) => totalWidth + $(slide).outerWidth(true), 0,
  );
};
/**
 * Updates enabled/disabled state of buttons based on current slider position.
 * @return {void}
 */
const updateButtonState = ($el, opts) => {
  const $slides = $el.find(`${BASE_SELECTOR}__item`);
  const $prev = $el.closest(WRAPPER_SELECTOR).find('.previous');
  const $next = $el.closest(WRAPPER_SELECTOR).find('.next');

  // Calculate container width and total slides width
  const containerWidth = $el.outerWidth();
  const totalScrollableWidth = calculateTotalScrollableWidth($slides, opts);

  // Calculate current scroll position and determine boundaries
  const currentScrollLeft = $el.scrollLeft();
  const lastSlideOffset = totalScrollableWidth - containerWidth;

  if (isRTL) {
    // In RTL, scrollLeft is negative as you scroll leftwards,
    // so the "end" is the most negative value
    const mostNegativeScrollLeft = -totalScrollableWidth + containerWidth;

    // Determine whether on first or last slide in RTL mode
    // Near zero in RTL mode means visual start
    const onFirstSlide = currentScrollLeft >= -1;
    // Near max negative value means visual end
    const onLastSlide = currentScrollLeft <= mostNegativeScrollLeft + 1;

    // Finally, update button state for RTL
    $prev.prop('disabled', onFirstSlide);
    $prev.attr('aria-hidden', onFirstSlide);
    $next.prop('disabled', onLastSlide);
    $next.attr('aria-hidden', onLastSlide);
  } else {
    // Determine whether on first or last slide in LTR mode
    const onFirstSlide = currentScrollLeft <= 0;
    const onLastSlide = currentScrollLeft >= lastSlideOffset;

    // Finally, update button state for LTR
    $prev.prop('disabled', onFirstSlide);
    $prev.attr('aria-hidden', onFirstSlide);
    $next.prop('disabled', onLastSlide);
    $next.attr('aria-hidden', onLastSlide);
  }
};

/**
 * Checks if the image is in the viewport and loads it if necessary
 * @param {jQuery} $lazyImage - jQuery wrapped image element with 'data-lazy' attribute
 * @param {Object} windowRect - Object containing viewport boundaries
 * @param {number} windowRect.top - Viewport's top position (window.scrollY)
 * @param {number} windowRect.bottom - Viewport's bottom position (scrollY + innerHeight)
 * @param {number} windowRect.left - Viewport's left position (window.scrollX)
 * @param {number} windowRect.right - Viewport's right position (scrollX + innerWidth)
 */
const loadImageIfInWindowViewport = ($lazyImage, windowRect) => {
  // Get the image's position and size
  const imgTop = $lazyImage.offset().top;
  const imgBottom = imgTop + $lazyImage.outerHeight();
  const imgLeft = $lazyImage.offset().left;
  const imgRight = imgLeft + $lazyImage.outerWidth();

  // Check if the image is within the viewport
  const isInVerticalViewport = imgBottom > windowRect.top && imgTop < windowRect.bottom;
  const isInHorizontalView = imgRight > windowRect.left && imgLeft < windowRect.right;

  // If the image is in the viewport and not loaded yet, load it
  if (isInVerticalViewport && isInHorizontalView) {
    $lazyImage.attr('src', $lazyImage.attr('data-lazy'));
    if ($lazyImage.attr('data-srcset')) {
      $lazyImage.attr('srcset', $lazyImage.attr('data-srcset'));
    }
    $lazyImage.addClass('loaded');
  }
};


const showImage = ($el, opts, key) => {
  if (!isContainerInWindowViewport($el)) return;

  const $lazyImages = $el.find('img[data-lazy]').not('.loaded');
  if ($lazyImages.length === 0) {
    $window.off(`scroll.horizontalScroll-${key}`);
    $el.off(`scroll.horizontalScroll-${key}`);
  }
  if (opts.verticalLazy) {
    // Calculate the viewport boundaries
    const windowRect = {
      top: window.scrollY,
      bottom: window.scrollY + window.innerHeight,
      left: window.scrollX,
      right: window.scrollX + window.innerWidth,
    };
    $lazyImages.each(function eachLazyImageInWindow() {
      const $lazyImage = $(this);
      loadImageIfInWindowViewport($lazyImage, windowRect);
    });
  } else {
    $lazyImages.each(function eachLazyImage() {
      const $lazyImage = $(this);
      if (isInViewport($lazyImage, $el)) {
        $lazyImage.attr('src', $lazyImage.attr('data-lazy'));
        if ($lazyImage.attr('data-srcset')) {
          $lazyImage.attr('srcset', $lazyImage.attr('data-srcset'));
        }
        $lazyImage.addClass('loaded');
      }
    });
  }
};

const getScrollStorageKey = (opts) => `${SCROLL_POSITION_STORAGE_PREFIX}${opts.preserveScrollKey || window.location.pathname}`;

const saveScrollPosition = ($el, opts) => {
  try {
    sessionStorage.setItem(getScrollStorageKey(opts), $el.scrollLeft());
  } catch (e) { /* sessionStorage unavailable */ }
};

const restoreScrollPosition = ($el, opts) => {
  try {
    const saved = sessionStorage.getItem(getScrollStorageKey(opts));
    if (saved !== null) {
      $el.scrollLeft(parseInt(saved, 10));
      updateButtonState($el, opts);
      updateSlideState($el);
    }
  } catch (e) { /* sessionStorage unavailable */ }
};

const onWindowScroll = ($el, opts, key) => {
  $window.on(`scroll.horizontalScroll-${key}`, throttle(() => showImage($el, opts, key), 200));
};
/**
 * Animates the horizontal scroll to specified index.
 * @param  {number} index  The slide index to which to move.
 * @return {void}
 */
const moveSlider = (index, $el, $slides, opts) => {
  const isWithinBounds = index >= 0 && index < $slides.length;

  if (isWithinBounds) {
    // Calculate new scroll position based on slide width
    const slideWidth = $slides.eq(index).outerWidth(true);
    const scrollDirection = isRTL ? -1 : 1;
    const scrollPosition = slideWidth * index * scrollDirection;
    updateButtonState($el, opts);


    $el.animate({ scrollLeft: scrollPosition }, 'fast', () => {
      // After scroll animation is complete, update index and set focus on the first focusable
      // element inside the visible slide
      $el.data('currentSlideIndex', index);
      updateSlideState($el);
      const $visibleSlide = $slides.eq(index);
      $visibleSlide.find('a, button, input, [tabindex="0"]').first().focus();
    });
  }
};
/**
 * Gets index of currently visible slide.
 * @return {number}  Index of currently visible slide.
 */
const getCurrentSlideIndex = ($el) => {
  // If in-memory data has an index, use it. If not, use fallback code.
  const index = $el.data('currentSlideIndex');
  if (typeof index === 'number') return index;

  let currentIndex = 0;
  const $slides = $el.find(`${BASE_SELECTOR}__item`);

  $slides.each(function checkInView(viewIndex) {
    const viewportStatus = isInHorizontalViewport(this);
    if (viewportStatus.fullyInViewport) {
      currentIndex = viewIndex;
      return false;
    }
    return true;
  });

  return currentIndex;
};

/**
 * Moves slider depending on keyboard input.
 * @return {void}
 */
const handleButtonPress = (event, $el, $buttons, opts) => {
  const $slides = $el.find(`${BASE_SELECTOR}__item`);

  const key = Keyboard.parseKey(event);

  if (key === 'ENTER' || key === 'SPACE' || event.type === 'click') {
    event.preventDefault();

    const $button = $(event.target);
    const currentIndex = getCurrentSlideIndex($el);
    let targetIndex;
    if (isRTL) {
      const hasPreviousClass = $button.hasClass('previous');
      if (hasPreviousClass) {
        targetIndex = Math.max(currentIndex - 1, 0);
      } else {
        targetIndex = Math.min(currentIndex + 1, $slides.length - 1);
      }
    } else {
      const hasPreviousClass = $button.hasClass('previous');
      if (hasPreviousClass) {
        targetIndex = currentIndex - 1;
      } else {
        targetIndex = currentIndex + 1;
      }
    }

    moveSlider(targetIndex, $el, $slides, opts);
  }
};
const onKeyDown = ($el, $a11yButtons, opts) => $a11yButtons.on('keydown', (evt) => handleButtonPress(evt, $el, $a11yButtons, opts));
const onClick = ($el, $a11yButtons, opts) => $a11yButtons.on('click', (evt) => handleButtonPress(evt, $el, $a11yButtons, opts));

const onButtonFocus = ($a11yButtons) => {
  $a11yButtons.on('focus', function removeAriaHidden() {
    $(this).attr('aria-hidden', false);
  });
};
const onButtonBlur = ($a11yButtons) => {
  $a11yButtons.on('blur', function addAriaHidden() {
    $(this).attr('aria-hidden', true);
  });
};

const scrollTrackingEvent = ($el, opts) => () => {
  const $image = $el.find('img');
  const imageIndexes = [];
  $image.each(function eachImage() {
    const $thisImage = $(this);
    const imageIndex = $thisImage.closest('.horizontal-scroll__item').index();
    if (isInViewport($thisImage, $el)) {
      imageIndexes.push(imageIndex + 1);
    }
  });
  const carouselEvent = new CustomEvent('carouselChange');
  carouselEvent.tracking = {
    type: opts.type,
    currentSlide: imageIndexes[imageIndexes.length - 1],
    pageUrl: window.location.href,
    pageTitle: document.title,
    element: $el,
  };
  window.dispatchEvent(carouselEvent);
};
const onHorizontalScroll = ($el, key, opts) => {
  const trackingScroll = scrollTrackingEvent($el, opts);
  // event listener handles the displaying of images
  $($el).on(`scroll.horizontalScroll-${key}`, throttle(() => showImage($el, opts, key), 200));
  $($el).on(`scroll.horizontalScrollState-${key}`, throttle(() => {
    updateSlideState($el);
    updateButtonState($el, opts);
  }, 200));
  // event listener used to track marketing tracking numbers when the users scrolls.
  $($el).on(`scroll.horizontalEventScroll-${key}`, debounce(trackingScroll, 500));
  // persist scroll position for back navigation
  if (opts.preserveScrollPosition) {
    $($el).on(`scroll.horizontalScrollPosition-${key}`, debounce(() => saveScrollPosition($el, opts), 200));
  }
};

const addAccessibleCarousel = ($el, opts) => {
  $el.data('currentSlideIndex', 0);
  wrapHorizontalScrollContent($el);
  updateSlideState($el);
  updateButtonState($el, opts);
};

const addEventListeners = ($el, key, opts) => {
  const $a11yButtons = $el.closest(WRAPPER_SELECTOR).find('> button');

  onWindowScroll($el, opts, key);
  onHorizontalScroll($el, key, opts);
  onKeyDown($el, $a11yButtons, opts);
  onClick($el, $a11yButtons, opts);
  onButtonFocus($a11yButtons);
  onButtonBlur($a11yButtons);
};

const init = ($el, opts) => {
  // key - creates unique name for the event namespace, so it can be later disabled
  const key = `key-${Math.ceil(Math.random() * 10000000000)}`;
  $el.find('img[data-lazy]').addClass('horizontal-scroll__lazy-image');
  addAccessibleCarousel($el, opts);
  addEventListeners($el, key, opts);
  showImage($el, opts, key);
  if (opts.preserveScrollPosition) {
    restoreScrollPosition($el, opts);
    // save position when a nav link is clicked so the destination page can share it
    $el.on(`click.horizontalScrollPosition-${key}`, 'a', () => saveScrollPosition($el, opts));
  }
};

export default function horizontalScroll(el, options = {}) {
  const $el = $(el);
  const opts = mergeOptions(DEFAULTS, options, $el);
  const showImages = init($el, opts);
  return {
    showImages: () => showImages(),
  };
}

registerJQueryPlugin(PLUGIN_NAME, horizontalScroll);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/horizontal-scroll.js